import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import {
  fetchBillingStatus,
  hasLiveSubscription,
  invalidateBillingStatus,
  isBlocked,
  startCheckout,
} from '../lib/billingFlow';
import { invalidatePlanAccess } from '../hooks/usePlanAccess';
import { BILLING_INTRO, BILLING_TRIAL_BANNER, ENTERPRISE_PITCH, ENTERPRISE_AFTER } from '../i18n/billingFlow';
import DowngradeImpact from './DowngradeImpact';
import { BILLING_PAYMENT_FAILED, BILLING_STOP_RULE, BILLING_SUBSCRIPTION_INACTIVE } from '../i18n/billingStop';
import { SELF_HOSTED_SPEC_SHORT } from '../i18n/selfHosted';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CreditCard, Zap, CheckCircle2, Clock, Users, Globe, AlertTriangle, Server } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';

// Human labels for the catalog's display-feature keys.
const FEATURE_LABELS = {
  full_tracker_core: 'Full tracker core',
  unlimited_clicks: 'Unlimited clicks',
  landing_editor: 'Landing page editor',
  online_support: 'Online support',
  click_api: 'Click API + API keys',
  capi_facebook: 'Facebook CAPI',
  capi_all_providers: 'All 9 CAPI providers',
  automation: 'Automation rules',
  custom_metrics: 'Custom metrics',
  conversion_types: 'Custom conversion types',
  report_by_conversion_date: 'Report by conversion date',
  audit_log: 'Audit log',
  white_label: 'White label branding',
  personal_manager: 'Personal manager',
  priority_support: 'Priority support',
  onboarding_call: 'Onboarding call',
  optimizer: 'Automatic flow optimiser',
  ml_antifraud: 'Learning bot detection',
  lead_forms: 'Lead forms into a CRM',
  rbac_matrix: 'Roles and 31 permissions',
  sso: 'Single sign on',
  migration_help: 'We move you off your old tracker',
  self_hosted: 'Install on your own server',
  dedicated_cloud: 'Or we set it up for you, on request',
};

// What the Enterprise card lists; the rest is "everything in Network".
const ENTERPRISE_HIGHLIGHTS = [
  'self_hosted',
  'dedicated_cloud',
  'white_label',
  'sso',
  'personal_manager',
  'priority_support',
  'migration_help',
];

// Readable forms for the counted allowances shown on each card.
const RETENTION_LABELS = {
  90: '90 days of history',
  365: '1 year of history',
  730: '2 years of history',
};

export default function Billing() {
  const { t } = useT();
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [interval, setInterval_] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const checkout = params.get('checkout') || (params.get('ok') ? 'success' : null);
  const [confirm, setConfirm] = useState(checkout === 'success' ? 'waiting' : null);

  const load = async () => {
    const [s, p] = await Promise.allSettled([
      axios.get(`${API}/billing/status`),
      axios.get(`${API}/billing/plans`),
    ]);
    if (s.status === 'fulfilled') setStatus(s.value.data);
    else setStatus({});
    if (p.status === 'fulfilled') setCatalog(p.value.data);
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  // Back from Stripe: the webhook usually lands within seconds. Poll until the
  // account is unlocked, then open the workspace.
  useEffect(() => {
    if (confirm !== 'waiting') return undefined;
    let stopped = false;
    const startedAt = Date.now();
    const tick = async () => {
      if (stopped) return;
      const s = await fetchBillingStatus(API, { fresh: true });
      if (stopped) return;
      if (hasLiveSubscription(s) || (s.enabled === false && !s.unavailable)) {
        invalidateBillingStatus();
        invalidatePlanAccess();
        toast.success(t('Payment confirmed. Welcome aboard!'));
        navigate('/dashboard', { replace: true });
        return;
      }
      if (Date.now() - startedAt > 90000) {
        setConfirm('slow');
        return;
      }
      setTimeout(tick, 2500);
    };
    tick();
    return () => {
      stopped = true;
    };
  }, [confirm, navigate, t]);

  const subscribe = async (planKey, iv = interval) => {
    setBusy(true);
    try {
      await startCheckout(API, planKey, iv);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    try {
      const r = await axios.post(`${API}/billing/portal`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  if (!loaded) return <div className="p-10 text-muted-foreground">{t('Loading…')}</div>;

  const plans = catalog?.plans || [];
  // Enterprise gets its own wide card below the self-serve editions.
  const tierPlans = plans.filter((p) => p.key !== 'enterprise');
  const enterprise = plans.find((p) => p.key === 'enterprise');
  const entIntervals = (enterprise?.intervals || ['monthly', 'annual']).filter((k) => enterprise?.pricing?.[k]);
  const usage = status?.usage || {};
  const state = status?.state || {};
  const plan = status?.plan || {};
  const sub = status?.subscription || {};
  const enabled = Boolean(catalog?.enabled);
  const trialDays = Number(catalog?.trial_days) || 0;
  const blocked = isBlocked(status);
  const liveSub = hasLiveSubscription(status);
  const entCurrent = Boolean(enterprise) && plan.key === 'enterprise' && liveSub;
  const neverSubscribed = !sub.stripe_subscription_id;
  const trialAvailable = enabled && trialDays > 0 && !sub.trial_used && neverSubscribed;
  const trialEnds = sub.trial_ends_at ? new Date(sub.trial_ends_at) : null;
  const inStripeTrial = liveSub && trialEnds && trialEnds.getTime() > Date.now();

  const fmtLimit = (v) => (v === null || v === undefined ? t('Unlimited') : v.toLocaleString());
  const statusTone = {
    trialing: 'bg-primary/15 text-primary border-primary/30',
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    exempt: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    past_due: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    expired: 'bg-red-500/15 text-red-400 border-red-500/30',
    canceled: 'bg-red-500/15 text-red-400 border-red-500/30',
  }[state.status] || 'bg-primary/15 text-primary border-primary/30';

  return (
    <div className="px-8 py-10 max-w-7xl">
      <XpnsrPageHeader
        kicker={t('Billing')}
        title={t('Plans & billing')}
        subtitle={t(BILLING_INTRO, { n: trialDays || 7, next: (trialDays || 7) + 1 })}
      />
      <p className="mb-6 mt-2 text-sm text-muted-foreground">{t(BILLING_STOP_RULE)}</p>

      {!enabled && (
        <Card className="mb-6 border-amber-500/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm text-muted-foreground">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            {t('Billing is not activated on this server yet — all features are open. Plans below show what will apply once payments go live.')}
          </CardContent>
        </Card>
      )}

      {confirm && (
        <Card className="mb-6 border-primary/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-primary" />
            <span>
              {confirm === 'waiting'
                ? t('Confirming your payment…')
                : t('Payment received, but confirmation is taking longer than usual. Refresh this page in a minute.')}
            </span>
          </CardContent>
        </Card>
      )}
      {enabled && inStripeTrial && (
        <Card className="mb-6 border-primary/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm">
            <Zap className="w-4 h-4 text-primary" />
            <span>{t(BILLING_TRIAL_BANNER, { date: trialEnds.toLocaleDateString() })}</span>
          </CardContent>
        </Card>
      )}
      {enabled && blocked && neverSubscribed && !confirm && (
        <Card className="mb-6 border-primary/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm">
            <CreditCard className="w-4 h-4 text-primary" />
            <span>
              {checkout === 'canceled'
                ? t('Payment was not completed. Choose a plan to continue.')
                : t('Choose a plan to open your workspace.')}
              {sub.trial_used ? ' ' + t('Your free trial has already been used, so this plan is billed today.') : ''}
            </span>
          </CardContent>
        </Card>
      )}
      {enabled && blocked && !neverSubscribed && (state.status === 'expired' || state.status === 'canceled' || state.status === 'unpaid' || state.status === 'incomplete_expired') && (
        <Card className="mb-6 border-red-500/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span>{t(BILLING_SUBSCRIPTION_INACTIVE)}</span>
          </CardContent>
        </Card>
      )}
      {enabled && state.status === 'past_due' && (
        <Card className="mb-6 border-red-500/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span>{t(BILLING_PAYMENT_FAILED)}</span>
          </CardContent>
        </Card>
      )}

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <CreditCard className="w-4 h-4" /> {t('Current plan')}
            <Badge className={`border ${statusTone}`}>{plan.label || '—'}</Badge>
            <Badge variant="outline">{t(state.status || 'trialing')}</Badge>
            {sub.cancel_at_period_end && <Badge variant="outline">{t('cancels at period end')}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('Users')}:</span>
              <span className="font-mono">{usage.users ?? 1} / {fmtLimit(usage.users_limit)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('Custom domains')}:</span>
              <span className="font-mono">{usage.domains ?? 0} / {fmtLimit(usage.domains_limit)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('History')}:</span>
              <span className="font-mono">
                {plan.limits?.retention_days
                  ? t(RETENTION_LABELS[plan.limits.retention_days] || 'History kept')
                  : t('Unlimited')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('Clicks this month')}:</span>
              <span className="font-mono">{(usage.clicks_this_month || 0).toLocaleString()} · {t('unlimited')}</span>
            </div>
          </div>
          {sub.stripe_customer_id && (
            <Button variant="outline" onClick={openPortal}>{t('Manage billing')}</Button>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 mb-4">
        {['monthly', 'semiannual', 'annual'].map((key) => {
          const label = key === 'monthly'
            ? t('Monthly')
            : key === 'semiannual'
              ? `6 ${t('months')} −10%`
              : `12 ${t('months')} −20%`;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setInterval_(key)}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                interval === key
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
              }`}
              data-testid={`billing-interval-${key}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {tierPlans.map((p) => {
          const pricing = p.pricing?.[interval] || {};
          const isCurrent = p.key === plan.key && liveSub;
          const planTrial = trialAvailable && Number(p.trial_days) > 0;
          return (
            <Card
              key={p.key}
              className={isCurrent ? 'border-primary/60 ring-1 ring-primary/25 bg-primary/[0.03]' : ''}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Zap className="w-4 h-4 text-primary" /> {p.label}
                  {!isCurrent && enabled && !liveSub && (
                    <Badge variant="outline" className={planTrial ? 'border-primary/40 text-primary' : ''}>
                      {planTrial ? t('{n} days free', { n: trialDays }) : t('Billed today')}
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge className="bg-primary/15 text-primary border border-primary/30">{t('Current')}</Badge>
                  )}
                </CardTitle>
                <div className="pt-1">
                  <span className="text-2xl font-semibold">${pricing.per_month_usd ?? p.monthly_usd}</span>
                  <span className="text-muted-foreground text-sm"> / {t('month')}</span>
                  {interval !== 'monthly' && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      ${pricing.total_usd} {t('billed every')} {pricing.months} {t('months')}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5 text-sm mb-5">
                  <li className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-primary" />
                    {fmtLimit(p.limits?.users)} {t('user(s)')}
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    {p.limits?.retention_days
                      ? t(RETENTION_LABELS[p.limits.retention_days] || 'History kept')
                      : t('History kept forever')}
                  </li>
                  <li className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-primary" />
                    {fmtLimit(p.limits?.domains)} {t('domain(s)')}
                  </li>
                  {(p.display || []).map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                      {t(FEATURE_LABELS[f] || f)}
                    </li>
                  ))}
                </ul>
                {liveSub &&
                tierPlans.findIndex((x) => x.key === plan.key) > tierPlans.findIndex((x) => x.key === p.key) ? (
                  <DowngradeImpact plan={p.key} label={p.label} />
                ) : null}
                {isCurrent ? (
                  <Button variant="outline" className="w-full" onClick={openPortal}>{t('Manage')}</Button>
                ) : liveSub ? null : (
                  <Button
                    className="w-full"
                    onClick={() => subscribe(p.key)}
                    disabled={busy || !enabled}
                    data-testid={`billing-choose-${p.key}`}
                  >
                    {planTrial ? t('Start {n}-day free trial', { n: trialDays }) : t('Subscribe')}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {enterprise && (
        <Card
          className={`mt-5 ${entCurrent ? 'border-primary/60 ring-1 ring-primary/25 bg-primary/[0.03]' : 'border-primary/30'}`}
          data-testid="billing-enterprise"
        >
          <CardContent className="py-6 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-base font-semibold flex-wrap">
                <Server className="w-4 h-4 text-primary" /> {enterprise.label}
                {entCurrent ? (
                  <Badge className="bg-primary/15 text-primary border border-primary/30">{t('Current')}</Badge>
                ) : enabled && !liveSub ? (
                  <Badge variant="outline">{t('Billed today')}</Badge>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t(ENTERPRISE_PITCH)}</p>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
                {ENTERPRISE_HIGHLIGHTS.filter((f) => (enterprise.display || []).includes(f)).map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                    {t(FEATURE_LABELS[f] || f)}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">{t(ENTERPRISE_AFTER)}</p>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="enterprise-server-spec">
                {t(SELF_HOSTED_SPEC_SHORT)}
              </p>
            </div>
            <div className="flex flex-col gap-2 lg:min-w-[260px]">
              {entCurrent ? (
                <Button variant="outline" onClick={openPortal}>{t('Manage')}</Button>
              ) : liveSub ? null : (
                entIntervals.map((key) => {
                  const pr = enterprise.pricing?.[key] || {};
                  return (
                    <div key={key} className="flex flex-col gap-0.5">
                      <Button
                        variant={key === 'annual' ? 'default' : 'outline'}
                        onClick={() => subscribe('enterprise', key)}
                        disabled={busy || !enabled}
                        data-testid={`billing-choose-enterprise-${key}`}
                      >
                        ${pr.per_month_usd ?? enterprise.monthly_usd} / {t('month')}
                        {key === 'annual' ? ` · 12 ${t('months')} −20%` : ''}
                      </Button>
                      {key === 'annual' && pr.total_usd ? (
                        <span className="text-xs text-muted-foreground text-center">
                          ${pr.total_usd} {t('billed every')} 12 {t('months')}
                        </span>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {liveSub && (
        <p className="mt-6 text-sm text-muted-foreground">
          {t('To switch plans, contact support — we move you without losing paid days.')}
        </p>
      )}
    </div>
  );
}
